'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { sprintf } from 'sprintf-js';
import classnames from 'classnames';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import Modal from 'react-bootstrap/Modal';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';

import BookmarkWidget from '@uw-it-sis/lib-react-myplan/lib/BookmarkWidget';
import AddCourseToPlanForm from '@uw-it-sis/lib-react-myplan/lib/AddCourseToPlanForm';
import {normalize} from '@uw-it-sis/lib-react-myplan/lib/CourseUtils';
import CourseMessages from "@uw-it-sis/lib-react-myplan/lib/CourseMessages";
import LinkBuilder from "@uw-it-sis/lib-react-myplan/lib/LinkBuilder";

/**
 * Displays course bookmark data.
 */
class Course extends Component {

    constructor(props) {
        super(props);
        this.props = props;
        // Don't initialize state based on props.
        this.state = {
            showModal: false,
            addPlanFormActive: false
        };

        this.handleAddToPlanClick = this.handleAddToPlanClick.bind(this);
    }

    /**
     * Click handler for the "add to plan" button.
     */
    handleAddToPlanClick = () => this.setState({showModal: true, addPlanFormActive: true});
    resetPlanFormActive  = () => this.setState({showModal: false, addPlanFormActive: false});
    showModal = () => this.setState({showModal: true});

    /**
     * Callback for when the overlay is closed (by clicking away or whatever).
     * This should reset the component.
     */
    hideModal = () => this.setState({showModal: false, addPlanFormActive: false});

    render() {
        const course = this.props.course;
        const plannedCourses = this.props.plannedCourses;
        const normalizedCode = normalize(course.code);

        let courseCredit = null;
        if (course.credit) courseCredit = "(" + course.credit + ")";

        let scheduledTerms = "Not currently offered";
        if (course.scheduledTerms.length) {
            let scheduledTermList = course.scheduledTerms.map((term, i) => {
                return (
                    <li className="list-inline-item" key={i}>
                        {TermUtils.makeTermBadge(term, i)}
                    </li>
                );
            });
            scheduledTerms = <ul className="list-inline mb-0">{scheduledTermList}</ul>;
        }

        let genEdReqTitle, genEdReqData;
        if (course.genEdRequirements.length) {
            genEdReqTitle = <dt className="col-12 col-md-3 text-start text-md-end fw-bold">Gen Edu Req</dt>;
            genEdReqData = <dd className="col-12 col-md-9">{course.genEdRequirements.join(", ")}</dd>;
        }

        let tags = /<(\w*)\s(.*?)['"\d\w\s]\/>/g;

        let courseReqs = "None";
        if (course.hasOwnProperty("requisites") && course.requisites.length > 0) {
            // Data has self closing tags which some how ends up wrapping text that follows it, so replace all self
            // closing tags with the same element and its own closing tag.
            courseReqs = course.requisites.join(" ").replace(tags, '<$1 $2></$1>');
        }

        let recommendedPrep = null;
        if (course.recommendedPrep) {
            recommendedPrep =
                <>
                    <dt className="col-12 col-md-3 text-start text-md-end fw-bold">Recommended Prep</dt>
                    <dd className="col-12 col-md-9">{LinkBuilder.linkify(course.recommendedPrep, true)}</dd>
                </>
        }

        let courseDescription = course.courseDescription.replace(tags, '<$1 $2></$1>');

        let termsOffered = "Check with the department or your adviser for more information about this course.";
        if (course.termsOffered.length) {
            termsOffered = course.termsOffered.join(", ");
        }

        let cdpLink =
            <Link title={normalize(course.code) + " " + course.courseTitle}
                  to={{pathname: sprintf("/courses/%s%s", course.subjectArea, course.courseNumber), search: "?id=" + course.courseId}}>
                {normalize(course.code)} &ndash; {course.courseTitle} {courseCredit}
            </Link>;

        let button = null;
        let overlay = null;
        if (! appStore.isAdviser()) {
            button = <Button variation="primary" onClick={this.handleAddToPlanClick}>Add to Plan</Button>;

            if (this.state.addPlanFormActive) {
                overlay =
                    <Modal id={"modal-course-" + course.id}
                           show={this.state.showModal}
                           onHide={this.hideModal}
                           onExited={this.resetPlanFormActive}
                           className="course-modal"
                           aria-labelledby={"modal-course-" + course.id + "-title"}
                           centered>
                        <Modal.Header closeButton>
                            <Modal.Title id={"modal-course-" + course.id + "-title"}>
                                {cdpLink}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <AddCourseToPlanForm course={course} onCancel={this.hideModal} analyticsContext="Course-Bookmarked-Course"/>
                        </Modal.Body>
                    </Modal>;
            }
        }
        let courseMessages = <CourseMessages messages={this.props.courseMessage}/>

        let bookmarkwidget = null;
        // If bookmarked course has been added to plan remove the delete icon
        if (! plannedCourses[normalizedCode]) {
            bookmarkwidget =
                <BookmarkWidget toggle={false} component="" code={normalize(course.code)}
                                type="kuali.lu.type.CreditCourse"/>
        }

        return (
            <>
                <li className="p-3 rounded-3 bg-gray-100">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2 className="h3 mb-0">
                            <Link title={normalize(course.code) + " " + course.courseTitle}
                                  to={{pathname: sprintf("/courses/%s%s", course.subjectArea, course.courseNumber), search: "?id=" + course.courseId}}>
                                {normalize(course.code)} &ndash; {course.courseTitle} {courseCredit}
                            </Link>
                        </h2>
                        {bookmarkwidget}
                    </div>
                    <dl className="description-term-colon row">
                        <dt className="col-12 col-md-3 text-start text-md-end fw-bold">Scheduled for</dt>
                        <dd className="col-12 col-md-9">{scheduledTerms}</dd>
                        <dt className="col-12 col-md-3 text-start text-md-end fw-bold">Projected for</dt>
                        <dd className="col-12 col-md-9">{termsOffered}</dd>
                        <dt className="col-12 col-md-3 text-start text-md-end fw-bold">Course Prerequisite</dt>
                        <dd className="col-12 col-md-9" dangerouslySetInnerHTML={{ __html: courseReqs }}/>
                        {recommendedPrep}
                        {genEdReqTitle}
                        {genEdReqData}
                        <dt className="sr-only">Course Description</dt>
                        <dd className="col-12" dangerouslySetInnerHTML={{ __html: courseDescription }}/>
                    </dl>
                    {courseMessages}
                    <ButtonToolbar className="align-items-center">
                        {button}
                    </ButtonToolbar>
                </li>
                {overlay}
            </>
        );
    }
}

Course.propTypes = {
    course: PropTypes.object.isRequired,
    courseMessage: PropTypes.string.isRequired
};

Course.defaultProps = {
    toggle: false
};

export default Course;